import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, Rocket, Brain, Archive, RefreshCw, ChevronRight,
  Calendar, Target, Globe, Map, Users, BarChart3, Zap,
  AlertCircle, Clock, Loader2,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface SavedPlan {
  id: number;
  storeName: string;
  topCategories: string[];
  topItems: string[];
  digitalTab: object;
  localTab: object;
  internationalTab: object;
  createdAt: string;
}

interface MarketingCenterProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  storeName: string;
  onTriggerLunaConsult?: () => void;
  onReplayPlan?: (plan: { topCategories: string[]; topItems: string[]; storeName: string }) => void;
}

type CenterTab = 'consultant' | 'vault';

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('si-LK', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const PlanCard = ({
  plan,
  onReplay,
}: {
  plan: SavedPlan;
  onReplay: () => void;
  key?: React.Key;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className="group relative rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:border-cyan-500/30 hover:bg-white/[0.07] transition-all p-4 cursor-pointer"
    onClick={onReplay}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-8 h-8 shrink-0 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <Target size={14} className="text-cyan-400" />
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-bold text-white truncate">{plan.storeName}</p>
          <p className="text-[9px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
            <Clock size={8} />{fmtDate(plan.createdAt)}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onReplay(); }}
        className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[9px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
      >
        නැවත බලන්න <ChevronRight size={9} />
      </button>
    </div>

    {plan.topCategories?.length > 0 && (
      <div className="flex flex-wrap gap-1 mt-3">
        {plan.topCategories.slice(0, 4).map((cat, i) => (
          <span key={i} className="px-2 py-0.5 rounded-full bg-violet-500/10 border border-violet-500/15 text-violet-300 text-[9px] font-medium">
            {cat}
          </span>
        ))}
        {plan.topItems?.slice(0, 3).map((item, i) => (
          <span key={`item-${i}`} className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/15 text-emerald-300 text-[9px] font-medium">
            {item}
          </span>
        ))}
      </div>
    )}

    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/[0.06]">
      {[
        { icon: Globe, label: 'ඩිජිටල්' },
        { icon: Map,   label: 'ප්‍රාදේශීය' },
        { icon: Users, label: 'ජාත්‍යන්තර' },
      ].map(({ icon: Icon, label }) => (
        <div key={label} className="flex items-center gap-1 text-slate-500 text-[9px]">
          <Icon size={9} /> {label}
        </div>
      ))}
    </div>
  </motion.div>
);

// ── Main Component ─────────────────────────────────────────────────────────────

export default function MarketingCenter({
  isOpen,
  onClose,
  storeApiKey,
  storeName,
  onTriggerLunaConsult,
  onReplayPlan,
}: MarketingCenterProps) {
  const [activeTab, setActiveTab] = useState<CenterTab>('consultant');
  const [savedPlans, setSavedPlans] = useState<SavedPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSavedPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cashierTok = localStorage.getItem('kangavena:cashierToken') || '';
      const headers: Record<string, string> = { 'x-store-api-key': storeApiKey };
      if (cashierTok) headers['Authorization'] = `Bearer ${cashierTok}`;
      const res = await fetch('/api/analytics/strategic-plans', { headers });
      if (res.ok) {
        const data = await res.json();
        setSavedPlans(Array.isArray(data.plans) ? data.plans : []);
      } else if (res.status === 403) {
        setError('සුරකිගත් සැලසුම් බැලීමට කළමනාකාර ප්‍රවේශය අවශ්‍යයි.');
      } else {
        setError('දත්ත ලබා ගැනීම අසාර්ථකයි. නැවත උත්සාහ කරන්න.');
      }
    } catch {
      setError('ජාල දෝෂයකි. සම්බන්ධතාව පරීක්ෂා කරන්න.');
    }
    setLoading(false);
  }, [storeApiKey]);

  useEffect(() => {
    if (isOpen && activeTab === 'vault') fetchSavedPlans();
  }, [isOpen, activeTab, fetchSavedPlans]);

  if (!isOpen) return null;

  const handleReplay = (plan: SavedPlan) => {
    onReplayPlan?.({
      topCategories: plan.topCategories || [],
      topItems: plan.topItems || [],
      storeName: plan.storeName || storeName,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="marketing-center-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9980] bg-slate-950/90 backdrop-blur-2xl flex flex-col"
      >
        {/* ── Header ── */}
        <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-violet-500/20 border border-cyan-500/30 flex items-center justify-center">
              <Rocket size={18} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-[15px] font-black text-white tracking-tight">අලෙවිකරණ මධ්‍යස්ථානය</p>
              <p className="text-[9px] text-cyan-400/60 font-mono uppercase tracking-widest">Marketing Center · {storeName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-all"
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Tab Bar ── */}
        <div className="shrink-0 flex items-center gap-2 px-6 pt-4 pb-2">
          {([
            ['consultant', Brain,   'Live Consultant'],
            ['vault',      Archive, 'Strategy Vault'],
          ] as const).map(([id, Icon, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold border transition-all ${
                activeTab === id
                  ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300'
                  : 'bg-white/[0.03] border-white/[0.07] text-slate-500 hover:text-slate-300 hover:border-white/15'
              }`}
            >
              <Icon size={12} /> {label}
              {id === 'vault' && savedPlans.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-[8px] font-black">
                  {savedPlans.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <AnimatePresence mode="wait">

            {/* ── Tab 1: Live Consultant ── */}
            {activeTab === 'consultant' && (
              <motion.div
                key="consultant"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex flex-col gap-6 max-w-2xl mx-auto pt-4"
              >
                {/* Hero CTA */}
                <div className="rounded-2xl bg-gradient-to-br from-cyan-500/10 via-violet-500/5 to-transparent border border-cyan-500/20 p-6 flex flex-col items-center text-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                    <BarChart3 size={28} className="text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-[18px] font-black text-white">Luna AI උපාය මාර්ගික උපදේශකය</p>
                    <p className="text-[12px] text-slate-400 mt-1 leading-relaxed">
                      ඔබේ ගබඩා දත්ත විශ්ලේෂණය කරමින් ඩිජිටල්, ප්‍රාදේශීය, සහ ජාත්‍යන්තර
                      අලෙවිකරණ රාමු නිර්මාණය කරයි.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      onTriggerLunaConsult?.();
                      onClose();
                    }}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-[12px] font-black transition-all shadow-lg shadow-cyan-500/20 active:scale-95"
                  >
                    <Zap size={14} /> උපාය මාර්ගික සැලසුම ආරම්භ කරන්න
                  </button>
                </div>

                {/* Instructions */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { icon: Brain,  title: 'Luna ට කියන්න', desc: '"ව්‍යාපාර දියුණු කරන්න" කියලා' },
                    { icon: Target, title: 'රාමුව ලැබේ',    desc: 'SWOT, PESTEL, Porter, Ansoff, CAGE සහ තව' },
                    { icon: Archive,title: 'සුරකිය හැක',    desc: '"සැලැස්ම සුරකිමු" — Vault එකේ රඳවාගනු' },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4">
                      <Icon size={14} className="text-cyan-400 mb-2" />
                      <p className="text-[11px] font-bold text-white">{title}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>

                {/* Voice hint */}
                <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-500/8 border border-violet-500/15">
                  <Zap size={12} className="text-violet-400 shrink-0" />
                  <p className="text-[10px] text-slate-400 leading-relaxed">
                    <span className="text-violet-300 font-bold">හඬ විධානය:</span> Luna ජීවී ගිණුමේ සිට කෙළින්ම{' '}
                    <span className="text-white font-bold">"ව්‍යාපාර දියුණු කරන්න"</span> කියන්න. සැලසුම සෙකන්ඩ් 10කින් සකස් වේ.
                  </p>
                </div>
              </motion.div>
            )}

            {/* ── Tab 2: Strategy Vault ── */}
            {activeTab === 'vault' && (
              <motion.div
                key="vault"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="flex flex-col gap-4 max-w-3xl mx-auto"
              >
                {/* Vault header */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[14px] font-black text-white">සුරකිගත් උපාය මාර්ග</p>
                    <p className="text-[10px] text-slate-500">ඕනෑම සැලසුමක් ක්ලික් කරන්න — සම්පූර්ණ රාමුව නැවත ලැබේ</p>
                  </div>
                  <button
                    onClick={fetchSavedPlans}
                    disabled={loading}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-400 hover:text-white text-[9px] font-bold transition-all disabled:opacity-50"
                  >
                    <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> නැවුම් කරන්න
                  </button>
                </div>

                {/* States */}
                {loading && (
                  <div className="flex flex-col items-center gap-3 py-16">
                    <Loader2 size={24} className="text-cyan-400 animate-spin" />
                    <p className="text-[11px] text-slate-500">දත්ත ලබා ගනිමින්...</p>
                  </div>
                )}

                {!loading && error && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/8 border border-rose-500/20">
                    <AlertCircle size={14} className="text-rose-400 shrink-0" />
                    <p className="text-[11px] text-rose-300">{error}</p>
                  </div>
                )}

                {!loading && !error && savedPlans.length === 0 && (
                  <div className="flex flex-col items-center gap-4 py-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.07] flex items-center justify-center">
                      <Archive size={22} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-slate-400">Vault එක හිස්ව ඇත</p>
                      <p className="text-[10px] text-slate-600 mt-1">
                        Luna ට "ව්‍යාපාර දියුණු කරන්න" කිව් පසු "සැලැස්ම සුරකිමු" යැයි කියන්න.
                      </p>
                    </div>
                  </div>
                )}

                {!loading && !error && savedPlans.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {savedPlans.map(plan => (
                      <PlanCard key={plan.id} plan={plan} onReplay={() => handleReplay(plan)} />
                    ))}
                  </div>
                )}
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
